/* ============================================================
   TASK-236 (#354, EPIC-023 pricing v2) — pure helpers behind
   GenerationLevelPicker (components/GenerationLevelPicker.jsx).

   No React, no fetch: testable under plain vitest (this repo has no
   jsdom — see src/lib/adminCredits.test.js for the same convention).

   Mirrors two backend contracts:
   - ai-router's GenerationLevel type (generation-level.ts):
     'economy' | 'standard' | 'premium'.
   - ai-router's GET /internal/credits/prices response (get-credit-
     prices.ts), proxied by the gateway at GET /api/credits/prices
     (routes/credit-prices.ts): { creditUnitCents, prices: [{ operation,
     level, priceCents, active }] }. `operation` is 'text' | 'image' |
     'video' (credit-prices.ts's DEFAULT_CREDIT_PRICES keys).
   ============================================================ */

export const GENERATION_LEVELS = ['economy', 'standard', 'premium'];

// The picker's OWN default, distinct from ai-router's technical default
// ('standard' — generation-level.ts's DEFAULT_GENERATION_LEVEL, used only
// when a request omits `level` entirely). Economy is the safe UI default:
// PRICING-MODEL-V2.md §2 makes Economy-level text UNLIMITED/free inside the
// Posting tariff, so defaulting a first-time picker to Economy never
// surprises a user with a credit charge before they've deliberately opted
// into a pricier tier.
export const DEFAULT_GENERATION_LEVEL = 'economy';

export function isGenerationLevel(value) {
  return GENERATION_LEVELS.includes(value);
}

/** Normalizes any input to a valid level, or `fallback` (default: DEFAULT_GENERATION_LEVEL) if invalid. */
export function normalizeGenerationLevel(value, fallback = DEFAULT_GENERATION_LEVEL) {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return isGenerationLevel(normalized) ? normalized : fallback;
}

/**
 * Builds the `level`/`metadata.level` fields to spread into a generation
 * request payload (createBatch, regenerate, regenerate-image). When `enabled`
 * is false (picker hidden — see lib/flags.js's generationLevelPickerEnabled,
 * owner decision 2026-09-08) returns `{}`: spreading that into a payload
 * literal adds neither key, so JSON.stringify never sends `level`, and
 * content-worker's/ai-router's own resolveGenerationLevel falls back to their
 * default ("standard") — byte-identical to how these requests behaved before
 * the picker existed. Kept pure (no window/localStorage read) so callers
 * decide `enabled` once via the flag and this stays trivially testable.
 */
export function generationLevelPayloadFields(level, enabled) {
  return enabled ? { level, metadata: { level } } : {};
}

function storageKey(userId) {
  return `dathent:generationLevel:${userId || 'anon'}`;
}

/**
 * Reads the persisted per-user level. `storage` is injected (not read from
 * `window` here) so this stays testable without jsdom/localStorage — pass
 * `window.localStorage` at the call site. Returns null (not the default) on
 * any error or when nothing/invalid was stored, so callers can distinguish
 * "no preference yet" from "explicitly economy".
 */
export function loadStoredGenerationLevel(userId, storage) {
  try {
    const raw = storage?.getItem?.(storageKey(userId));
    return raw ? normalizeGenerationLevel(raw, null) : null;
  } catch {
    return null;
  }
}

/** Persists the user's level choice. Returns false (never throws) if `level` is invalid or storage fails. */
export function saveGenerationLevel(userId, level, storage) {
  const normalized = normalizeGenerationLevel(level, null);
  if (!normalized) return false;
  try {
    storage?.setItem?.(storageKey(userId), normalized);
    return true;
  } catch {
    return false;
  }
}

/** The level a generation form should start on: the user's last choice, else DEFAULT_GENERATION_LEVEL. */
export function pickDefaultGenerationLevel(userId, storage) {
  return loadStoredGenerationLevel(userId, storage) || DEFAULT_GENERATION_LEVEL;
}

/**
 * Pure lookup into the `/api/credits/prices` `prices[]` array: the price (in
 * cents) for one operation x level, or null if absent/inactive. `operation`
 * is 'text' | 'image' | 'video' (see file header).
 */
export function findLevelPriceCents(prices, operation, level) {
  if (!Array.isArray(prices)) return null;
  const row = prices.find((p) => p && p.operation === operation && p.level === level && p.active !== false);
  return typeof row?.priceCents === 'number' ? row.priceCents : null;
}

const CURRENCY_SYMBOLS = { EUR: '€', USD: '$', GBP: '£' };

/**
 * Formats a credit price (in cents; 1 credit = 100 cents = 1 unit of
 * `currency`, TASK-135 ledger canon) as a short display string, e.g.
 * priceCents=2 -> "€0.02", priceCents=100 -> "€1". Returns null (never a
 * broken/blank string) for a missing or invalid price so callers can render
 * a neutral "—" placeholder instead of "€NaN".
 */
export function formatCreditPriceCents(priceCents, currency = 'EUR') {
  if (typeof priceCents !== 'number' || !Number.isFinite(priceCents) || priceCents < 0) return null;
  const amount = priceCents / 100;
  const amountStr = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  const cur = String(currency || 'EUR').toUpperCase();
  const symbol = CURRENCY_SYMBOLS[cur];
  return symbol ? `${symbol}${amountStr}` : `${amountStr} ${cur}`;
}
